import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";

import type DateRange from "shared/helpers/DateRange";
import { type MediumName } from "shared/helpers/media";
import {
  type SortOptionField,
  type SortOptionOrder,
} from "shared/helpers/mentions";
import {
  type StreamRequestFilters,
  type StreamRequestFiltersSortOption,
} from "shared/helpers/StreamFilters";
import PublicInsightsService from "shared/services/api/PublicInsightsService";
import { fetchMentions } from "shared/services/fetch/fetchMentions";
import { type Stream } from "shared/types";
import type {
  Mention,
  MentionType,
  SocialMentionType,
  SyndicationKey,
} from "shared/types/mentions";

export interface FetchServiceOptions {
  allFields?: boolean;
  bundle_broadcast?: boolean;
  case_sensitive?: boolean;
  collapse_syndicated?: boolean;
  documentIds?: SyndicationKey[];
  endpoint?: string;
  excerpt_radius?: number;
  excludedMedia?: MediumName[];
  fields?: string;
  filter_most_read_sources?: boolean;
  filterSetId?: number;
  includeBookmarkSyndication?: boolean;
  keepAlreadyLoadedMentions?: boolean;
  keyword?: string;
  keywords?: string;
  limit?: number;
  loadLatestMentions?: boolean;
  media?: MediumName[];
  page?: number;
  pageSize?: number;
  range: DateRange;
  search?: StreamRequestFilters;
  shouldTrackUniversalSearch?: boolean;
  signal?: AbortSignal;
  skipMediaValidation?: boolean;
  sort_options?: StreamRequestFiltersSortOption[];
  sortBy?: SortOptionField;
  sortOrder?: SortOptionOrder;
  source_group_keys?: string[];
  source_keys?: string[];
  stream_ids?: number[];
  syndicationKeys?: string[];
  timeout?: number;
  types?: (MentionType | SocialMentionType)[];
  useMilliseconds?: boolean;
  whole_text?: boolean;
}

export interface FetchServiceResponseHeaders {
  "x-page"?: number;
  "x-total-count": number;
  "x-total-pages": number;
}

export type FetchServiceAxiosResponse<T = Mention[]> = AxiosResponse<T>;

export interface FetchServiceEmptyResponse<T = Mention[]> {
  data: T;
  headers: FetchServiceResponseHeaders;
}

export interface FetchServiceGetParams<
  OptionsType extends FetchServiceOptions = FetchServiceOptions,
  StreamType = Stream,
> {
  mentions: Mention[];
  options: OptionsType;
  stream: StreamType;
}

// Define the common interface that both services implement
export interface FetchService<
  GetResponse = Mention[],
  OptionsType extends FetchServiceOptions = FetchServiceOptions,
  StreamType = Stream,
> {
  get: (
    params: FetchServiceGetParams<OptionsType, StreamType>
  ) => Promise<
    | FetchServiceAxiosResponse<GetResponse>
    | FetchServiceEmptyResponse<GetResponse>
  >;
  getIds: (
    options: FetchServiceGetParams
  ) => Promise<
    | AxiosResponse<SyndicationKey[]>
    | FetchServiceEmptyResponse<SyndicationKey[]>
  >;
}

export function createEmptyIdsResponse(): AxiosResponse<SyndicationKey[]> {
  return {
    config: { headers: {} } as InternalAxiosRequestConfig,
    data: [],
    headers: {},
    status: 200,
    statusText: "OK",
  };
}

export function createFetchService({
  publicAccessKey,
}: { publicAccessKey?: string } = {}): FetchService {
  if (publicAccessKey) {
    const publicService = new PublicInsightsService(publicAccessKey);

    return {
      get: (params: FetchServiceGetParams<FetchServiceOptions>) =>
        publicService.streamMentions(params),
      // In public mode, getIds is not supported - return empty result
      // This allows components to call it safely without checking isPublicInsights
      getIds: () => Promise.resolve(createEmptyIdsResponse()),
    };
  }

  return fetchMentions;
}
